// API-backed portfolio feed. Replaces the static imageService as the source of
// truth for what the homepage (and later, category pages) render. Returns images
// with their responsive variant set so the gallery can emit <img srcset>.

export interface FeedVariant {
  label: string;
  width: number;
  height: number;
  format: string;
  url: string; // absolute path on this origin (served by CloudFront)
}

export interface FeedImage {
  id: string;         // public token (stable, opaque)
  token: string;
  title: string;
  description: string; // maps to alt for now
  alt: string;
  ratio: string;       // CSS aspect-ratio friendly (e.g. "2649 / 4000")
  orientation: string | null;
  category: string | null;
  portfolioLink: string;
  src: string;         // default (largest webp) src
  variants: FeedVariant[];
}

interface FeedResponse {
  surface: string;
  images: Array<{
    token: string;
    title: string;
    alt: string;
    ratio: string | null;
    orientation: string | null;
    category: string | null;
    portfolioLink: string;
    src: string;
    variants: FeedVariant[];
  }>;
}

// Normalize "2649:4000" or "2649/4000" → "2649 / 4000" for CSS aspect-ratio.
function normalizeRatio(ratio: string | null): string {
  if (!ratio) return '';
  const parts = ratio.split(/[:/]/).map(s => s.trim()).filter(Boolean);
  if (parts.length === 2) return `${parts[0]} / ${parts[1]}`;
  return ratio;
}

// Build a srcset string from WebP variants ("<url> <width>w, ...").
// Kept for back-compat with components still emitting a single <img srcset>.
export function buildSrcset(variants: FeedVariant[]): string {
  return buildSrcsetFor(variants, 'webp');
}

// Build a srcset string from variants of ONE format ("<url> <width>w, ...").
export function buildSrcsetFor(variants: FeedVariant[], format: string): string {
  return (variants || [])
    .filter(v => v.format === format && v.width)
    .sort((a, b) => a.width - b.width)
    .map(v => `${v.url} ${v.width}w`)
    .join(', ');
}

export interface PictureSource {
  type: string;   // MIME type for <source type>
  srcset: string; // width-descriptor srcset for that format
}

export interface PictureModel {
  sources: PictureSource[]; // ordered: AVIF first, then WebP (best-first)
  jpgSrc: string;           // fallback <img src> (largest jpg, else largest webp, else provided default)
}

/**
 * Build an ordered <picture> model from a variant set: AVIF + WebP <source>
 * elements (only when that format has variants) plus a JPG fallback src.
 * `defaultSrc` is used when no jpg/webp variant exists (e.g. legacy single src).
 */
export function pictureSourcesFor(variants: FeedVariant[] | undefined, defaultSrc = ''): PictureModel {
  const vs = variants || [];
  const sources: PictureSource[] = [];
  const avif = buildSrcsetFor(vs, 'avif');
  const webp = buildSrcsetFor(vs, 'webp');
  if (avif) sources.push({ type: 'image/avif', srcset: avif });
  if (webp) sources.push({ type: 'image/webp', srcset: webp });

  const largest = (fmt: string): string => {
    const m = vs.filter(v => v.format === fmt && v.width).sort((a, b) => b.width - a.width)[0];
    return m?.url || '';
  };
  const jpgSrc = largest('jpg') || largest('webp') || defaultSrc;
  return { sources, jpgSrc };
}

class PortfolioService {
  // Same-origin API (CloudFront /api/* → API Gateway). Empty base = relative.
  private base = import.meta.env.VITE_API_BASE || '';

  async getFeed(surface = 'home'): Promise<FeedImage[]> {
    const res = await fetch(`${this.base}/api/portfolio/feed?surface=${encodeURIComponent(surface)}`, {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error(`feed ${res.status}`);
    const data: FeedResponse = await res.json();
    return (data.images || []).map(img => ({
      id: img.token,
      token: img.token,
      title: img.title || '',
      description: img.alt || img.title || '',
      alt: img.alt || img.title || '',
      ratio: normalizeRatio(img.ratio),
      orientation: img.orientation,
      category: img.category,
      portfolioLink: img.portfolioLink || '',
      src: img.src || (img.variants[0]?.url ?? ''),
      variants: img.variants || [],
    }));
  }

  /** Curated images for a category page surface (e.g. 'headshots'). */
  getCategoryFeed(category: string): Promise<FeedImage[]> {
    return this.getFeed(`category:${category}`);
  }
}

export const portfolioService = new PortfolioService();
